import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import DashboardTemplate from "../Common/DashboardTemplate";
import { TextField, CircularProgress, Button, Paper, Typography, Divider } from "@material-ui/core";
import axios from 'axios';
import { ServiceLocator } from "../../Services/ServiceLocator";
import { AuthenticationService } from '../../Services/AuthenticationService';

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
    profileContainer: {
        maxWidth: 400,
        margin: '0 auto',
        padding: 20,
    },
    section: {
        marginBottom: 10,
    },
    sectionTitle: {
        marginBottom: 15,
        color: '#333',
        fontWeight: 'bold',
    },
    formField: {
        marginBottom: 20,
        width: '100%',
    },
    passwordSection: {
        backgroundColor: '#f5f5f5',
        padding: 20,
        borderRadius: 8,
        marginTop: 20,
    },
    errorText: {
        color: '#d32f2f',
        fontSize: 14,
        marginTop: 5,
    },
    successText: {
        color: '#388e3c',
        fontSize: 14,
        marginTop: 5,
    },
    buttonContainer: {
        margin: '0 auto'
    },
});

class MyProfile extends React.Component {
    state = {
        loading: true,
        saving: false,
        user: {},
        // Profile fields
        firstName: '',
        lastName: '',
        email: '',
        // Password fields
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        // Validation
        errors: {},
        successMessage: '',
    };

    constructor(props) {
        super(props);
        const authService = AuthenticationService.getInstance();
        const token = authService.getToken();
        if (token) {
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
        }
    }

    componentDidMount() {
        this.loadUserData();
    }

    loadUserData = async () => {
        try {
            const user = AuthenticationService.getInstance().getUser();
            if (!user) {
                this.props.history.push('/login');
                return;
            }

            this.setState({
                user,
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email || '',
                loading: false
            });
        } catch (error) {
            console.error('Error loading user data:', error);
            this.setState({ loading: false });
        }
    };

    handleInputChange = (field, value) => {
        this.setState({
            [field]: value,
            errors: { ...this.state.errors, [field]: '' },
            successMessage: ''
        });
    };

    validateProfileFields = () => {
        const errors = {};
        
        if (!this.state.firstName.trim()) {
            errors.firstName = 'First name is required';
        }
        
        if (!this.state.lastName.trim()) {
            errors.lastName = 'Last name is required';
        }
        
        if (!this.state.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(this.state.email)) {
            errors.email = 'Please enter a valid email address';
        }

        return errors;
    };

    validatePasswordFields = () => {
        const errors = {};
        
        if (this.state.newPassword || this.state.confirmPassword || this.state.currentPassword) {
            if (!this.state.currentPassword) {
                errors.currentPassword = 'Current password is required';
            }
            
            if (!this.state.newPassword) {
                errors.newPassword = 'New password is required';
            } else if (this.state.newPassword.length < 8) {
                errors.newPassword = 'Password must be at least 8 characters long';
            }
            
            if (!this.state.confirmPassword) {
                errors.confirmPassword = 'Please confirm your new password';
            } else if (this.state.newPassword !== this.state.confirmPassword) {
                errors.confirmPassword = 'Passwords do not match';
            }
        }

        return errors;
    };

    handleProfileSave = async () => {
        const errors = this.validateProfileFields();
        
        if (Object.keys(errors).length > 0) {
            this.setState({ errors });
            return;
        }

        this.setState({ saving: true, errors: {}, successMessage: '' });

        try {
            const userService = ServiceLocator.Instance.getUserService();
            const updatedUser = await userService.updateProfile({
                firstName: this.state.firstName.trim(),
                lastName: this.state.lastName.trim(),
                email: this.state.email.trim()
            });

            // Update session storage
            const currentUser = AuthenticationService.getInstance().getUser();
            const updatedUserData = { ...currentUser, ...updatedUser };
            AuthenticationService.getInstance().setUser(updatedUserData);

            window.$alertSuccess('Profile updated successfully!');

            this.setState({
                saving: false
            });
        } catch (error) {
            console.error('Error updating profile:', error);
            window.$alertError('Failed to update profile');
            this.setState({
                saving: false
            });
        }
    };

    handlePasswordChange = async () => {
        const errors = this.validatePasswordFields();
        
        if (Object.keys(errors).length > 0) {
            this.setState({ errors });
            return;
        }

        this.setState({ saving: true, errors: {}, successMessage: '' });

        try {
            const userService = ServiceLocator.Instance.getUserService();
            await userService.changePassword({
                currentPassword: this.state.currentPassword,
                newPassword: this.state.newPassword,
                confirmPassword: this.state.confirmPassword
            });

            window.$alertSuccess('Password changed successfully!');

            // Clear password fields
            this.setState({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
                saving: false
            });
        } catch (error) {
            console.error('Error changing password:', error);
            window.$alertError('Failed to change password');
            this.setState({
                saving: false
            });
        }
    };

    handleKeyPress = (event, action) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (action === 'profile') {
                this.handleProfileSave();
            } else if (action === 'password') {
                this.handlePasswordChange();
            }
        }
    };

    render() {
        const { classes } = this.props;
        const { loading, saving, errors, successMessage } = this.state;

        if (loading) {
            return (
                <DashboardTemplate title="My Profile">
                    <div className="main-dashboard-root">
                        <CircularProgress />
                    </div>
                </DashboardTemplate>
            );
        }

        return (
            <DashboardTemplate title="My Profile">
                <div className="main-dashboard-root">
                    <div className={classes.profileContainer}>
                        <Paper elevation={2} style={{ padding: 30 }}>
                            {/* Profile Information Section */}
                            <div className={classes.section}>
                                <Typography variant="h5" className={classes.sectionTitle}>
                                    Profile Information
                                </Typography>
                                
                                <TextField
                                    label="First Name"
                                    value={this.state.firstName}
                                    onChange={(e) => this.handleInputChange('firstName', e.target.value)}
                                    onKeyPress={(e) => this.handleKeyPress(e, 'profile')}
                                    variant="outlined"
                                    className={classes.formField}
                                    error={!!errors.firstName}
                                    helperText={errors.firstName}
                                    fullWidth
                                />
                                
                                <TextField
                                    label="Last Name"
                                    value={this.state.lastName}
                                    onChange={(e) => this.handleInputChange('lastName', e.target.value)}
                                    onKeyPress={(e) => this.handleKeyPress(e, 'profile')}
                                    variant="outlined"
                                    className={classes.formField}
                                    error={!!errors.lastName}
                                    helperText={errors.lastName}
                                    fullWidth
                                />
                                
                                <TextField
                                    label="Email"
                                    value={this.state.email}
                                    onChange={(e) => this.handleInputChange('email', e.target.value)}
                                    onKeyPress={(e) => this.handleKeyPress(e, 'profile')}
                                    variant="outlined"
                                    className={classes.formField}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    fullWidth
                                />

                                <div className={classes.buttonContainer}>
                                    {saving ?
                                        <CircularProgress />
                                        :
                                        <Button
                                            onClick={this.handleProfileSave}
                                            primary={true}
                                            className="success-button-lg"
                                            disabled={saving}
                                        >Save</Button>
                                    }
                                </div>
                            </div>
                        </Paper>
                    </div>
                    <div className={classes.profileContainer}>
                        <Paper elevation={2} style={{ padding: 30 }}>
                            {/* Password Change Section */}
                            <div className={classes.section}>
                                <Typography variant="h5" className={classes.sectionTitle}>
                                    Change Password
                                </Typography>
                                <TextField
                                    label="Current Password"
                                    type="password"
                                    value={this.state.currentPassword}
                                    onChange={(e) => this.handleInputChange('currentPassword', e.target.value)}
                                    onKeyPress={(e) => this.handleKeyPress(e, 'password')}
                                    variant="outlined"
                                    className={classes.formField}
                                    error={!!errors.currentPassword}
                                    helperText={errors.currentPassword}
                                    fullWidth
                                />
                                
                                <TextField
                                    label="New Password"
                                    type="password"
                                    value={this.state.newPassword}
                                    onChange={(e) => this.handleInputChange('newPassword', e.target.value)}
                                    onKeyPress={(e) => this.handleKeyPress(e, 'password')}
                                    variant="outlined"
                                    className={classes.formField}
                                    error={!!errors.newPassword}
                                    helperText={errors.newPassword || 'Password must be at least 8 characters long'}
                                    fullWidth
                                />
                                
                                <TextField
                                    label="Confirm New Password"
                                    type="password"
                                    value={this.state.confirmPassword}
                                    onChange={(e) => this.handleInputChange('confirmPassword', e.target.value)}
                                    onKeyPress={(e) => this.handleKeyPress(e, 'password')}
                                    variant="outlined"
                                    className={classes.formField}
                                    error={!!errors.confirmPassword}
                                    helperText={errors.confirmPassword}
                                    fullWidth
                                />

                                <div className={classes.buttonContainer}>
                                    {saving ?
                                        <CircularProgress />
                                        :
                                        <Button
                                            onClick={this.handlePasswordChange}
                                            primary={true}
                                            className="success-button-lg"
                                            disabled={saving || (!this.state.currentPassword && !this.state.newPassword && !this.state.confirmPassword)}
                                        >Change Password</Button>
                                    }
                                </div>
                            </div>

                            {/* Error and Success Messages */}
                            {errors.general && (
                                <Typography className={classes.errorText}>
                                    {errors.general}
                                </Typography>
                            )}
                            
                            {errors.password && (
                                <Typography className={classes.errorText}>
                                    {errors.password}
                                </Typography>
                            )}
                        </Paper>
                    </div>
                </div>
            </DashboardTemplate>
        );
    }
}

MyProfile.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(MyProfile); 